<template>
  <div class="tab-content">
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
        </div>
        <div>
          <h3 class="card-title">Armazenamento</h3>
          <p class="card-desc">Uso de espaço desta obra. O limite é definido pelo administrador geral.</p>
        </div>
      </div>

      <div v-if="loading" class="storage-loading">Carregando...</div>
      <p v-else-if="error" class="storage-error">{{ error }}</p>

      <template v-else-if="status">
        <div class="storage-bar-wrap">
          <div
            class="storage-bar-fill"
            :style="{ width: Math.min(100, status.pct) + '%', background: barColor }"
          />
        </div>
        <p class="storage-label">
          <strong>{{ formatMb(status.usedMb) }}</strong> usados de {{ formatMb(status.limitMb) }} ({{ Math.round(status.pct) }}%)
        </p>
        <p v-if="status.pct >= 90" class="storage-warn">
          Armazenamento quase cheio. Exclua arquivos/manuais antigos ou solicite aumento de limite ao administrador geral.
        </p>

        <div v-if="breakdownItems.length" class="storage-breakdown">
          <div v-for="item in breakdownItems" :key="item.key" class="sb-row">
            <span class="sb-dot" :style="{ background: item.color }" />
            <span class="sb-name">{{ item.label }}</span>
            <span class="sb-val">{{ formatMb(item.mb) }}</span>
          </div>
        </div>

        <button class="btn-refresh" type="button" :disabled="loading" @click="load">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Atualizar
        </button>
      </template>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { apiFetch } from '@/store/api'
import { breakdownToItems } from '@/utils/storageCategories'

interface StorageStatus {
  usedMb: number
  limitMb: number
  pct: number
  breakdown?: Record<string, number>
}

export default defineComponent({
  name: 'SystemTabArmazenamento',
  setup() {
    const status = ref<StorageStatus | null>(null)
    const loading = ref(true)
    const error = ref('')

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        status.value = await apiFetch<StorageStatus>('/storage-status')
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Erro ao carregar o armazenamento.'
      } finally {
        loading.value = false
      }
    }

    const barColor = computed(() => {
      const pct = (status.value?.pct || 0) / 100
      if (pct >= 0.9) return '#dc2626'
      if (pct >= 0.7) return '#f59e0b'
      return '#16a34a'
    })

    const breakdownItems = computed(() => breakdownToItems(status.value?.breakdown))

    const formatMb = (mb: number) => {
      if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB'
      return (mb ?? 0).toFixed(1) + ' MB'
    }

    onMounted(load)

    return { status, loading, error, load, barColor, formatMb, breakdownItems }
  }
})
</script>

<style scoped lang="scss">
.tab-content { display: flex; flex-direction: column; gap: 16px; }
.settings-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card,#fff); padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; }
.card-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 16px; border-bottom: 1px solid rgba(31,30,26,.07); }
.card-icon { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: rgba(31,30,26,.06); display: flex; align-items: center; justify-content: center; }
.card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); }
.card-desc { margin: 0; font-size: 12px; color: var(--ink-muted,#888); }

.storage-loading { font-size: 13px; color: var(--ink-muted); padding: 12px 0; }
.storage-error { font-size: 13px; color: #dc2626; margin: 0; }

.storage-bar-wrap { height: 12px; background: rgba(31,30,26,.08); border-radius: 99px; overflow: hidden; }
.storage-bar-fill { height: 100%; border-radius: 99px; transition: width .3s; }
.storage-label { font-size: 13px; color: var(--ink-muted); margin: 0; strong { color: var(--ink); } }
.storage-warn { font-size: 12.5px; color: #b45309; background: rgba(217,119,6,.08); border: 1px solid rgba(217,119,6,.25); border-radius: 8px; padding: 8px 12px; margin: 0; }

.storage-breakdown { display: flex; flex-direction: column; gap: 8px; border-top: 1px solid rgba(31,30,26,.07); padding-top: 14px; max-width: 380px; }
.sb-row { display: flex; align-items: center; gap: 9px; font-size: 13px; }
.sb-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.sb-name { color: var(--ink); flex: 1; }
.sb-val { color: var(--ink-muted); font-weight: 600; font-variant-numeric: tabular-nums; }

.btn-refresh {
  display: inline-flex; align-items: center; gap: 7px; align-self: flex-start;
  padding: 7px 14px; border-radius: 8px; cursor: pointer;
  border: 1px solid rgba(31,30,26,.15); background: none;
  color: var(--ink-muted); font-size: 12.5px; font-weight: 600;
  &:hover:not(:disabled) { border-color: var(--accent); color: var(--accent); }
  &:disabled { opacity: .5; }
}
</style>
